/**
 * Ficha de proveedor. Las pestañas viven en ?sub=.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { AdminConfirmDialog } from '@/components/shared/AdminConfirmDialog';
import { ResourceDetail } from '@/components/shared/ResourceDetail';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { esCount } from '@/lib/es-count';
import { formatDate, formatDateTime, formatEurFromCents, formatNumber } from '@/lib/format-es';
import { useQueryClient } from '@tanstack/react-query';
import { notify } from '@/lib/notify';
import { useStoreHome } from '../home/useStoreHome';
import { StoreOrdersPage } from '../orders/StoreOrdersPage';
import { recentSupplierOrders } from '../orders/orderListModel';
import { useStoreOrdersList } from '../orders/hooks/useStoreOrders';
import { CatalogAllowlistPanel } from '../catalog/CatalogAllowlistPanel';
import {
    useCatalogSuppliers,
    useCatalogSyncRuns,
    useRunCatalogSync,
    useSupplierActivity,
} from '../catalog/hooks/useStoreCatalog';
import type { CatalogSupplierRow } from '../catalog/types';
import { SupplierBrandTerms } from './SupplierBrandTerms';
import { SupplierConditionsForm } from './SupplierConditionsForm';
import { SupplierTemplateUpload } from './SupplierTemplateUpload';
import { SupplierActiveControl } from './SupplierActiveControl';
import { supplierFreshness } from './supplierFreshness';
import { supplierActivityLabel, syncNowBlockedReason } from './supplierActive';
import { PageNoticePanel } from '../PageNoticePanel';
import { rememberNotice, type PageNotice } from '../pageNotice';
import { refreshStoreAfter } from '../storeRefresh';
import { catalogSyncFinishedText, catalogSyncRunFiguresText, catalogSyncRunKindLabel, pendingBrandCount } from './syncResultText';
import { isFileTemplateSupplier } from './templateManual';
import { CONNECTOR_LABELS, publicFeedConfig, SCHEDULE_LABELS } from './supplierContact';
import { useUrlSubTab } from '../useUrlSubTab';

const TABS = ['resumen', 'conexion', 'importar', 'condiciones', 'pedidos'] as const;
type SupplierTab = (typeof TABS)[number];

const TAB_LABELS: Record<SupplierTab, string> = {
    resumen: 'Resumen',
    conexion: 'Conexión',
    importar: 'Importar productos',
    condiciones: 'Condiciones',
    pedidos: 'Pedidos',
};

function LaterNote({ children }: { children: string }) {
    return (
        <p className="rounded-lg border border-dashed border-border px-3 py-2 text-sm text-muted-foreground">
            {children}
        </p>
    );
}

export function SupplierDetailPage() {
    const { slug = '' } = useParams();
    const suppliersQ = useCatalogSuppliers();
    const supplier = (suppliersQ.data ?? []).find((row) => row.slug === slug);
    const [tab, setTab] = useUrlSubTab<SupplierTab>(TABS, 'resumen');

    if (suppliersQ.isLoading) {
        return (
            <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Cargando proveedor…
            </div>
        );
    }
    if (!supplier) {
        return (
            <p className="text-sm text-muted-foreground">
                No está el proveedor «{slug}».{' '}
                <Link to="/store/suppliers" className="text-primary underline">Volver al listado</Link>
            </p>
        );
    }

    const status = supplierFreshness({
        slug: supplier.slug,
        isActive: supplier.isActive,
        lastOkFinishedAt: supplier.lastOk?.finishedAt,
    });

    return (
        <ResourceDetail
            crumbs={[
                { label: 'Proveedores', to: '/store/suppliers' },
                { label: supplier.name },
            ]}
            title={supplier.name}
            badge={<StatusBadge status={status} />}
            actions={<SupplierActiveControl supplier={supplier} />}
        >
            <div className="flex flex-wrap gap-1 rounded-lg border border-border/60 bg-muted/30 p-1 w-fit">
                {TABS.map((id) => (
                    <button
                        key={id}
                        type="button"
                        onClick={() => setTab(id)}
                        className={cn(
                            'rounded-md px-3 py-1.5 text-sm font-medium whitespace-nowrap',
                            tab === id
                                ? 'border border-border/80 bg-card text-foreground shadow-sm'
                                : 'text-muted-foreground hover:text-foreground',
                        )}
                    >
                        {TAB_LABELS[id]}
                    </button>
                ))}
            </div>
            {tab === 'resumen' ? <SummaryTab supplier={supplier} status={status} /> : null}
            {tab === 'conexion' ? <ConnectionTab supplier={supplier} /> : null}
            {tab === 'importar' ? <ImportTab supplier={supplier} /> : null}
            {tab === 'condiciones' ? (
                <div className="space-y-8">
                    <SupplierConditionsForm supplier={supplier} />
                    <SupplierBrandTerms slug={supplier.slug} />
                </div>
            ) : null}
            {tab === 'pedidos' ? <StoreOrdersPage lockedOrigin={supplier.slug} /> : null}
        </ResourceDetail>
    );
}

function SummaryTab({ supplier, status }: { supplier: CatalogSupplierRow; status: string }) {
    const home = useStoreHome();
    const orders = useStoreOrdersList({
        view: 'all',
        q: '',
        page: 1,
        sort: 'created_at',
        dir: 'desc',
        origin: supplier.slug,
        limit: 20,
    });
    const recentOrders = recentSupplierOrders(orders.data?.rows ?? [], new Date());
    const collisions = (home.data?.brandCollisions ?? []).filter((row) => (
        row.supplierSlug === supplier.slug && row.showBrand === false && row.productCount > 0
    ));

    return (
        <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
                {supplier.isActive
                    ? 'El proveedor está activo: se puede sincronizar. Publicar una ficha es aparte.'
                    : 'El proveedor está inactivo: la sincronización no arranca. Actívalo arriba; las fichas no se publican solas.'}
            </p>
            <SupplierActivityList slug={supplier.slug} />
            <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div>
                    <dt className="text-xs text-muted-foreground">Fichas activas</dt>
                    <dd className="text-lg font-semibold tabular-nums">{formatNumber(supplier.productCount)}</dd>
                </div>
                <div>
                    <dt className="text-xs text-muted-foreground">Ventas</dt>
                    <dd className="text-lg font-semibold tabular-nums">{formatEurFromCents(supplier.salesCents ?? 0)}</dd>
                </div>
                <div>
                    <dt className="text-xs text-muted-foreground">Pedidos</dt>
                    <dd className="text-lg font-semibold tabular-nums">{formatNumber(supplier.orderCount ?? 0)}</dd>
                </div>
                <div>
                    <dt className="text-xs text-muted-foreground">Última sync correcta</dt>
                    <dd className="text-lg font-semibold tabular-nums">{formatDate(supplier.lastOk?.finishedAt)}</dd>
                </div>
            </dl>
            <ul className="space-y-2 text-sm">
                {status === 'sync_stale' ? (
                    <li className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-red-200">
                        La sincronización está caducada
                        {supplier.lastOk?.finishedAt ? ` (última correcta el ${formatDate(supplier.lastOk.finishedAt)})` : ''}.
                    </li>
                ) : null}
                {status === 'unsynced' ? (
                    <li className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-amber-100">
                        Todavía no hay una sincronización correcta.
                    </li>
                ) : null}
                {!supplier.orderContactEmail?.trim() ? (
                    <li className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-amber-100">
                        Sin email de pedidos. Se configura en Condiciones.
                    </li>
                ) : null}
                {collisions.map((row) => (
                    <li key={row.brand} className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-amber-100">
                        {esCount(row.productCount, 'producto')} con marca «{row.brand}», que es el proveedor.{' '}
                        <Link className="underline" to={`/store/products?view=brand&origin=${supplier.slug}`}>
                            Ver en Productos
                        </Link>
                    </li>
                ))}
            </ul>
            <section className="space-y-2">
                <h2 className="text-sm font-semibold">Últimos pedidos con líneas de este proveedor</h2>
                {recentOrders.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                        Ningún pedido todavía. Los checkouts abandonados no se listan aquí.
                    </p>
                ) : (
                    <ul className="divide-y divide-border rounded-xl border border-border">
                        {recentOrders.map((order) => (
                            <li key={order.id}>
                                <Link
                                    to={`/store/orders/${order.id}`}
                                    state={{ listSearch: `/store/suppliers/${supplier.slug}?sub=resumen` }}
                                    className="flex flex-wrap items-baseline justify-between gap-2 px-3 py-2 text-sm hover:bg-muted/40"
                                >
                                    <span className="font-mono">{order.order_number}</span>
                                    <span className="text-muted-foreground">{formatDate(order.created_at)}</span>
                                    <span className="tabular-nums">{formatEurFromCents(order.total_cents)}</span>
                                    <StatusBadge status={order.status} />
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
}

function ConnectionTab({ supplier }: { supplier: CatalogSupplierRow }) {
    const qc = useQueryClient();
    const runsQ = useCatalogSyncRuns(supplier.slug);
    const syncMut = useRunCatalogSync();
    const [pending, setPending] = useState<null | 'preview' | 'sync'>(null);
    const [notices, setNotices] = useState<PageNotice[]>([]);
    const awaiting = useRef<Map<number, boolean>>(new Map());
    const config = publicFeedConfig(supplier.feedConfig);
    const runs = runsQ.data ?? [];
    const syncBlocked = syncNowBlockedReason(supplier.isActive);
    const openRun = runs.find((row) => row.status === 'queued' || row.status === 'running') ?? null;

    const record = (notice: Omit<PageNotice, 'id'>) => {
        setNotices((prev) => rememberNotice(prev, notice));
        notify(notice);
    };

    useEffect(() => {
        for (const row of runs) {
            const dryRun = awaiting.current.get(row.id);
            if (dryRun == null) continue;
            if (row.status === 'queued' || row.status === 'running') continue;
            awaiting.current.delete(row.id);
            const notice = catalogSyncFinishedText({
                dryRun,
                runId: row.id,
                status: row.status,
                productsUpserted: row.productsUpserted,
                productsDeactivated: row.productsDeactivated,
                errorMessage: row.errorMessage,
                pendingBrands: pendingBrandCount(row.stats),
            });
            setNotices((prev) => rememberNotice(prev, notice));
            notify(notice);
            if (!dryRun && row.status === 'ok') {
                void refreshStoreAfter(qc, 'supplier-sync');
            }
        }
    }, [qc, runs]);

    const run = async () => {
        const dryRun = pending === 'preview';
        try {
            const res = await syncMut.mutateAsync({
                supplier: supplier.slug,
                dryRun,
                imageLimit: dryRun ? 0 : 40,
            });
            awaiting.current.set(res.runId, dryRun);
            record({ type: 'ok', text: `Ejecución #${res.runId} encolada` });
        } catch (error: unknown) {
            record({ type: 'err', text: error instanceof Error ? error.message : String(error) });
        } finally {
            setPending(null);
        }
    };

    return (
        <div className="space-y-4">
            <PageNoticePanel notices={notices} />
            {isFileTemplateSupplier(supplier) ? (
                <SupplierTemplateUpload supplier={supplier} onRecord={record} />
            ) : (
                <p className="text-sm text-muted-foreground">
                    Este proveedor se actualiza solo. La plantilla CSV es para el resto.
                </p>
            )}
            <dl className="grid gap-3 sm:grid-cols-2 text-sm">
                <div>
                    <dt className="text-xs text-muted-foreground">Conector</dt>
                    <dd>{CONNECTOR_LABELS[supplier.connectorType ?? ''] ?? supplier.connectorType ?? '—'}</dd>
                </div>
                <div>
                    <dt className="text-xs text-muted-foreground">Cadencia</dt>
                    <dd>
                        {isFileTemplateSupplier(supplier)
                            ? 'No hay cron. Se aplica al pulsar Sincronizar ahora'
                            : (SCHEDULE_LABELS[supplier.catalogSyncSchedule ?? ''] ?? supplier.catalogSyncSchedule ?? '—')}
                    </dd>
                </div>
            </dl>
            <p className="text-xs text-muted-foreground">Configuración del conector</p>
            <pre className="overflow-x-auto rounded-lg border border-border bg-muted/30 p-3 text-xs">
                {JSON.stringify(config, null, 2)}
            </pre>
            <div className="flex flex-wrap items-center gap-2">
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={openRun != null || syncMut.isPending}
                    onClick={() => setPending('preview')}
                >
                    Previsualizar sync
                </Button>
                <Button
                    type="button"
                    size="sm"
                    disabled={openRun != null || syncMut.isPending || syncBlocked != null}
                    title={syncBlocked ?? undefined}
                    onClick={() => setPending('sync')}
                >
                    Sincronizar ahora
                </Button>
                {openRun ? (
                    <p className="basis-full text-sm text-muted-foreground">
                        Ejecución #{openRun.id} en curso. Espera a que termine para lanzar otra.
                    </p>
                ) : syncBlocked ? (
                    <p className="basis-full text-sm text-muted-foreground">{syncBlocked}</p>
                ) : null}
            </div>
            <section className="space-y-2">
                <h2 className="text-sm font-semibold">Últimas ejecuciones</h2>
                {runs.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Sin ejecuciones.</p>
                ) : (
                    <ul className="divide-y divide-border rounded-xl border border-border">
                        {runs.map((runRow) => {
                            const preview = runRow.dryRun === true && runRow.status === 'ok';
                            const kind = catalogSyncRunKindLabel(runRow.dryRun);
                            return (
                            <li key={runRow.id} className="flex flex-wrap items-baseline justify-between gap-2 px-3 py-2 text-sm">
                                <span>#{runRow.id} · {formatDateTime(runRow.finishedAt || runRow.startedAt)}</span>
                                {kind ? <span className="text-xs font-semibold">{kind}</span> : null}
                                {preview ? null : <StatusBadge status={runRow.status} />}
                                <span className="text-xs text-muted-foreground">
                                    {catalogSyncRunFiguresText(runRow.dryRun, {
                                        upsert: formatNumber(runRow.productsUpserted),
                                        off: formatNumber(runRow.productsDeactivated),
                                        skip: formatNumber(runRow.productsSkipped),
                                    })}
                                </span>
                                {runRow.errorMessage ? (
                                    <span className="basis-full text-xs text-red-300">{runRow.errorMessage}</span>
                                ) : null}
                            </li>
                            );
                        })}
                    </ul>
                )}
            </section>
            <AdminConfirmDialog
                open={pending != null}
                title={pending === 'preview' ? 'Previsualizar sincronización' : 'Sincronizar ahora'}
                description={
                    pending === 'preview'
                        ? `Se consultará el feed de ${supplier.name} sin guardar productos.`
                        : `Se actualizarán precio, stock e imágenes de la selección de ${supplier.name}.`
                }
                confirmLabel={pending === 'preview' ? 'Previsualizar' : 'Sincronizar'}
                confirmDisabled={syncMut.isPending}
                onConfirm={() => { void run(); }}
                onCancel={() => { if (!syncMut.isPending) setPending(null); }}
            />
        </div>
    );
}

function SupplierActivityList({ slug }: { slug: string }) {
    const activity = useSupplierActivity(slug);
    const items = activity.data ?? [];
    return (
        <section className="space-y-2">
            <h2 className="text-sm font-semibold">Actividad</h2>
            {activity.isLoading ? (
                <p className="text-sm text-muted-foreground">Cargando actividad…</p>
            ) : items.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sin cambios de alta.</p>
            ) : (
                <ul className="divide-y divide-border rounded-xl border border-border">
                    {items.map((item) => (
                        <li key={item.id} className="flex flex-wrap items-baseline justify-between gap-2 px-3 py-2 text-sm">
                            <span>{supplierActivityLabel(item.action)}</span>
                            <span className="text-muted-foreground">{item.actorName}</span>
                            <span className="text-xs text-muted-foreground">{formatDateTime(item.createdAt)}</span>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}

function ImportTab({ supplier }: { supplier: CatalogSupplierRow }) {
    return (
        <div className="space-y-3">
            <LaterNote>
                Las reglas de precio y stock (T07) se colocarán junto a esta selección.
            </LaterNote>
            <CatalogAllowlistPanel
                supplier={supplier.slug}
                onMessage={(message) => notify({ type: message.type, text: message.text })}
            />
        </div>
    );
}
